/**
 * The reconnect banner.
 *
 * Spec 8.6: three minutes on a mobile network means drops are normal, not
 * exceptional — and the round clock does not pause while you are gone. So this
 * is a banner, never a modal and never a spinner over the board: the UI stays
 * live and interactive underneath, because the player may only have lost a
 * fraction of a second and the globe must still accept their tap.
 */

import type { ConnectionStatus } from '../net/transport';

export interface ConnectionBannerProps {
  readonly status: ConnectionStatus;
  readonly rttMs: number;
  readonly inMatch: boolean;
}

export function ConnectionBanner({
  status,
  rttMs,
  inMatch,
}: ConnectionBannerProps): React.JSX.Element | null {
  if (status === 'open' || status === 'idle') return null;

  const message =
    status === 'reconnecting'
      ? 'Connection lost. Reconnecting now, and the round clock keeps running.'
      : status === 'connecting'
        ? 'Connecting…'
        : status === 'failed'
          ? 'Could not reach the server.'
          : inMatch
            ? 'Disconnected.'
            : null;

  if (!message) return null;

  return (
    <div className="gg-banner" role="status" data-status={status}>
      <span className="gg-banner-dot" aria-hidden="true" />
      <span className="flex-1">{message}</span>
      {rttMs > 0 ? <span className="tabular-nums opacity-70">{Math.round(rttMs)}ms</span> : null}
    </div>
  );
}
